/**
 * Offline punch queue — Sprint 14, SDD §7.6
 *
 * When the API is unreachable, punch payloads are stored in IndexedDB.
 * On reconnect, queued punches replay against the API in order.
 *
 * Implements: FR-OFFLINE-02 (queue punches offline, sync on reconnect)
 *             FR-OFFLINE-04 (last-write-wins conflict resolution)
 *             NFR-AVAIL-02 (offline punch queuing)
 */

import { openDB, type IDBPDatabase } from 'idb';

// ── Types ────────────────────────────────────────────────────────────────

export interface QueuedPunch {
  id?: number;
  type: 'in' | 'out';
  data: Record<string, unknown>;
  queued_at: string;
  offline_origin: true;
}

export interface SyncResult {
  synced: number;
  conflicts: number;
  remaining: number;
}

type SyncListener = (pending: number) => void;

// ── Constants ────────────────────────────────────────────────────────────

const DB_NAME = 'dcvfd-offline';
const DB_VERSION = 1;
const STORE_NAME = 'pending-punches';

// ── Listeners for queue count changes ────────────────────────────────────

const listeners = new Set<SyncListener>();

export function onQueueChange(fn: SyncListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

async function notifyListeners(): Promise<void> {
  const count = await getPendingCount();
  listeners.forEach((fn) => fn(count));
}

// ── Database ─────────────────────────────────────────────────────────────

let dbPromise: Promise<IDBPDatabase> | null = null;

function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined';
  } catch {
    return false;
  }
}

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    if (!isIndexedDBAvailable()) {
      return Promise.reject(new Error('IndexedDB not available'));
    }
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        }
      },
    });
  }
  return dbPromise;
}

// ── Queue operations ─────────────────────────────────────────────────────

export async function queuePunch(type: 'in' | 'out', data: Record<string, unknown>): Promise<void> {
  try {
    const db = await getDB();
    const punch: QueuedPunch = {
      type,
      data,
      queued_at: new Date().toISOString(),
      offline_origin: true,
    };
    await db.add(STORE_NAME, punch);
    await notifyListeners();
  } catch {
    // IndexedDB unavailable — punch will be lost (best-effort)
  }
}

export async function getPendingPunches(): Promise<QueuedPunch[]> {
  try {
    const db = await getDB();
    return db.getAll(STORE_NAME);
  } catch {
    return [];
  }
}

export async function getPendingCount(): Promise<number> {
  try {
    const db = await getDB();
    return db.count(STORE_NAME);
  } catch {
    return 0;
  }
}

export async function removePunch(id: number): Promise<void> {
  try {
    const db = await getDB();
    await db.delete(STORE_NAME, id);
  } catch {
    // IndexedDB unavailable
  }
}

// ── Sync logic ───────────────────────────────────────────────────────────

let syncing = false;

export function isSyncing(): boolean {
  return syncing;
}

/**
 * Replay queued punches against the API in order.
 *
 * - 200/201: success → remove from queue
 * - 409: conflict (e.g., already clocked in by officer) → discard + log (FR-OFFLINE-04)
 * - Network error or other failure → stop, try again later
 */
export async function syncPendingPunches(): Promise<SyncResult> {
  if (syncing) return { synced: 0, conflicts: 0, remaining: await getPendingCount() };

  syncing = true;
  let syncedCount = 0;
  let conflictCount = 0;

  try {
    const pending = await getPendingPunches();

    for (const punch of pending) {
      try {
        const endpoint = punch.type === 'in' ? '/api/punch/in' : '/api/punch/out';
        const body = { ...punch.data, offline_origin: true, queued_at: punch.queued_at };

        const response = await fetch(endpoint, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (response.ok) {
          await removePunch(punch.id!);
          syncedCount++;
        } else if (response.status === 409) {
          // Conflict — discard the offline punch, log for admin review
          console.warn('[offline] Punch conflict discarded:', {
            type: punch.type,
            queued_at: punch.queued_at,
            data: punch.data,
          });
          await removePunch(punch.id!);
          conflictCount++;
        } else {
          // Server error — stop syncing, try again later
          break;
        }
      } catch {
        // Network still down — stop, we'll retry on next online event
        break;
      }
    }
  } finally {
    syncing = false;
    await notifyListeners();
  }

  const remaining = await getPendingCount();
  return { synced: syncedCount, conflicts: conflictCount, remaining };
}

// ── Online/offline event wiring ──────────────────────────────────────────

let initialized = false;

export function initOfflineSync(): void {
  if (initialized) return;
  initialized = true;

  window.addEventListener('online', () => {
    syncPendingPunches();
  });
}

// ── Check if we're online ────────────────────────────────────────────────

export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine !== false : true;
}

// ── Geolocation capture (Sprint 15, SDD §10.1, FR-GEO-01, FR-GEO-02) ───

export interface CapturedLocation {
  lat: number;
  lng: number;
  accuracy: number;
}

/**
 * Optionally capture GPS coordinates via the browser Geolocation API.
 * Returns null if geolocation is unavailable, permission is denied, or
 * the request times out. Never throws — a missing location must never
 * prevent a punch (FR-GEO-02, FR-GEO-03).
 */
export async function captureLocation(): Promise<CapturedLocation | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return null;

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
      () => resolve(null), // Permission denied or error → proceed without
      { enableHighAccuracy: true, timeout: 5000 },
    );
  });
}
